import { useEffect, useRef, useState } from 'react';
import {
  CreateInvestmentEntityRequest,
  EntityType,
  TrusteeCorporate,
  TrusteeIndividual,
} from '../../models/InvestmentEntity';
import {
  StructuredError,
  structuredErrorToFieldErrorPairs,
} from '../../services/BaseBackendService';
import {
  FieldDefinition,
  getFieldDefinitionsForEntityType,
} from '../../utils/entityFieldDefinitions';
import { TRUSTEE_VALIDATION_MESSAGES } from './createEntityStepConstants';
import { EntityFieldFocusRefs } from './EntitySpecificFieldInput';

export interface FormErrors {
  type?: string;
  [key: string]: string | undefined;
}

interface UseCreateEntityFormOptions {
  initialData?: CreateInvestmentEntityRequest;
  validationErrors?: { [key: string]: string };
  onEntityCreated: (entity: CreateInvestmentEntityRequest) => Promise<void>;
}

function clearTrusteeErrors(prev: FormErrors): FormErrors {
  const newErrors = { ...prev };
  delete newErrors.trusteeIndividuals;
  delete newErrors.trusteeCorporate;
  delete newErrors.trusteeCorporateCompanyName;
  delete newErrors.trusteeCorporateAcn;
  return newErrors;
}

export function useCreateEntityForm({
  initialData,
  validationErrors,
  onEntityCreated,
}: UseCreateEntityFormOptions) {
  const [formData, setFormData] = useState<CreateInvestmentEntityRequest>(
    initialData || { description: '' },
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [entitySpecificFields, setEntitySpecificFields] = useState<FieldDefinition[]>([]);
  const [trusteeIndividuals, setTrusteeIndividuals] = useState<TrusteeIndividual[]>([]);
  const [trusteeCorporate, setTrusteeCorporate] = useState<TrusteeCorporate | null>(null);

  const allErrors = { ...errors, ...validationErrors };

  const trusteeNameRef = useRef<HTMLInputElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const companyNameRef = useRef<HTMLInputElement>(null);
  const trusteeFirstNameRef = useRef<HTMLInputElement>(null);

  const focusRefs: EntityFieldFocusRefs = {
    trusteeName: trusteeNameRef,
    firstName: firstNameRef,
    companyName: companyNameRef,
  };

  const previousEntityTypeRef = useRef<EntityType | undefined>(formData.type);

  useEffect(() => {
    const previousType = previousEntityTypeRef.current;
    previousEntityTypeRef.current = formData.type;

    const fields = getFieldDefinitionsForEntityType(formData.type);
    setEntitySpecificFields(fields.map((field) => ({ ...field, error: undefined })));

    setFormData((prev) => ({
      type: prev.type,
      description: prev.description,
    }));

    setTrusteeIndividuals([]);
    setTrusteeCorporate({ companyName: '', acn: '' });

    setErrors((prev) => {
      const newErrors = { ...prev };
      if (previousType !== undefined) {
        getFieldDefinitionsForEntityType(previousType).forEach((field) => {
          delete newErrors[field.key];
        });
      }
      return clearTrusteeErrors(newErrors);
    });
  }, [formData.type]);

  useEffect(() => {
    if (formData.type === 'TRUST' || formData.type === 'SMSF') {
      setTimeout(() => {
        trusteeNameRef.current?.focus();
      }, 200);
    }

    if (formData.type === 'INDIVIDUAL') {
      setTimeout(() => {
        firstNameRef.current?.focus();
      }, 100);
    }

    if (formData.type === 'COMPANY' || formData.type === 'PARTNERSHIP') {
      setTimeout(() => {
        companyNameRef.current?.focus();
      }, 100);
    }
  }, [formData.type]);

  useEffect(() => {
    setTimeout(() => {
      firstNameRef.current?.focus();
    }, 100);
  }, []);

  const handleInputChange = (field: keyof CreateInvestmentEntityRequest, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (allErrors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSpecificFieldChange = (fieldKey: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));

    if (allErrors[fieldKey]) {
      setErrors((prev) => ({ ...prev, [fieldKey]: undefined }));
    }

    if (fieldKey === 'trusteeType') {
      if (value === 'INDIVIDUAL') {
        setTrusteeIndividuals([{ firstName: '', lastName: '' }]);
        setTrusteeCorporate(null);
      } else {
        setTrusteeIndividuals([]);
        setTrusteeCorporate({ companyName: '', acn: '' });
      }
      setErrors((prev) => clearTrusteeErrors(prev));
    }
  };

  const addTrusteeIndividual = () => {
    setTrusteeIndividuals((prev) => [...prev, { firstName: '', lastName: '' }]);
    setTimeout(() => {
      trusteeFirstNameRef.current?.focus();
    }, 100);
  };

  const removeTrusteeIndividual = (index: number) => {
    setTrusteeIndividuals((prev) => prev.filter((_, i) => i !== index));
  };

  const updateTrusteeIndividual = (
    index: number,
    field: keyof TrusteeIndividual,
    value: string,
  ) => {
    setTrusteeIndividuals((prev) =>
      prev.map((individual, i) => (i === index ? { ...individual, [field]: value } : individual)),
    );
  };

  const updateTrusteeCorporate = (field: keyof TrusteeCorporate, value: string) => {
    setTrusteeCorporate((prev) => ({
      companyName: prev?.companyName || '',
      acn: prev?.acn || '',
      [field]: value,
    }));

    if (errors.trusteeCorporateCompanyName || errors.trusteeCorporateAcn) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.trusteeCorporateCompanyName;
        delete newErrors.trusteeCorporateAcn;
        return newErrors;
      });
    }
  };

  const handleSubmit = async () => {
    const entityData: CreateInvestmentEntityRequest = {
      ...formData,
      description: formData.description?.trim() || undefined,
    };

    if ((formData.type === 'TRUST' || formData.type === 'SMSF') && formData.trusteeType) {
      const trusteeType = formData.trusteeType;
      if (trusteeType === 'INDIVIDUAL' && trusteeIndividuals.length > 0) {
        entityData.trusteeIndividuals = trusteeIndividuals;
      } else if (trusteeType === 'CORPORATE') {
        entityData.trusteeCorporate = trusteeCorporate || {
          companyName: '',
          acn: '',
        };
      }
    }

    try {
      await onEntityCreated(entityData);
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'structuredError' in error) {
        const backendErrors = structuredErrorToFieldErrorPairs(
          error as unknown as StructuredError,
          TRUSTEE_VALIDATION_MESSAGES,
        );
        setErrors(backendErrors);
      }
    }
  };

  const isTrustEntity = formData.type === 'TRUST' || formData.type === 'SMSF';
  const showTrusteeIndividuals = isTrustEntity && formData.trusteeType === 'INDIVIDUAL';
  const showTrusteeCorporate = isTrustEntity && formData.trusteeType === 'CORPORATE';
  const isFormValid = Boolean(formData.type && Object.keys(allErrors).length === 0);

  return {
    formData,
    errors,
    allErrors,
    entitySpecificFields,
    trusteeIndividuals,
    trusteeCorporate,
    focusRefs,
    trusteeFirstNameRef,
    handleInputChange,
    handleSpecificFieldChange,
    addTrusteeIndividual,
    removeTrusteeIndividual,
    updateTrusteeIndividual,
    updateTrusteeCorporate,
    handleSubmit,
    showTrusteeIndividuals,
    showTrusteeCorporate,
    isFormValid,
  };
}
